import {isLoggedIn} from '@github-ui/client-env'
import {CodingAssistantButton} from '@github-ui/coding-assistant-button'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useIssueState} from '@github-ui/issue-pr-state'
import {getIssueStateIcon} from '@github-ui/list-view-items-issues-prs/StateIcon'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {InfoIcon, KebabHorizontalIcon, LinkIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, SkeletonBox} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useEffect, useState} from 'react'

import {BUTTON_LABELS} from '../../constants/buttons'
import {TEST_IDS} from '../../constants/test-ids'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import styles from '../IssueViewer.module.css'
import type {OptionConfig} from '../OptionConfig'
import {StickySubscribeButton} from '../sections/subscription-section/StickySubscribeButton'
import {CreateIssueButtonShell} from './CreateIssueButtonShell'
import type {HeaderBlockedBySummaryFragment} from './HeaderBlockedBySummary'
import type {HeaderSubIssueSummaryFragment} from './HeaderSubIssueSummary'
import type {LinkedSecurityAlertsFragment} from './LinkedSecurityAlerts'
import type {
  StickyHeaderIssueTypeNode,
  StickyHeaderRepositoryNode,
  StickyHeaderRepositoryOwnerNode,
} from './sticky-header-query-types'
import {StickyHeaderChips} from './StickyHeaderChips'
import type {TrackedByFragment} from './TrackedBy'
import {ViewPositionIndicator} from './ViewPositionIndicator'

const StickyHeaderCreateIssueButtonLazy = React.lazy(async () => {
  const m = await import('@github-ui/issue-create/CreateIssueButton')
  return {default: m.CreateIssueButton}
})

export type StickyHeaderProps = {
  issueKey: StickyHeaderFragment | null | undefined
  linkedSecurityAlertsData?: LinkedSecurityAlertsFragment
  blockedBySecondaryData?: HeaderBlockedBySummaryFragment
  trackedBySecondaryData?: TrackedByFragment
  subIssueSecondaryData?: HeaderSubIssueSummaryFragment
  issueNumber: number
  ownerLogin: string
  repoName: string
  issueUrl: string
  optionConfig: OptionConfig
  isHeaderSticky: boolean
  onInfoClick: () => void
  infoButtonRef: React.Ref<HTMLButtonElement>
}

// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): widens nullable fields so the still-Relay
// parent data is assignable. Remove when IssueViewerSecondaryViewQuery leaves Relay.
export type StickyHeaderFragment = RelayCompat<
  Pick<
    IssueSecondaryNode,
    | 'state'
    | 'stateReason'
    | 'title'
    | 'url'
    | 'subIssuesSummary'
    | 'agentAssignments'
    | 'id'
    | 'threadSubscriptionChannel'
    | 'viewerThreadSubscriptionFormAction'
    | 'closedByPullRequestsReferences'
  > & {
    readonly repository: Pick<StickyHeaderRepositoryNode, 'isWritable' | 'nameWithOwner' | 'name' | 'isPrivate'> & {
      readonly owner: Pick<StickyHeaderRepositoryOwnerNode, 'login'>
    }
    readonly issueType: Pick<StickyHeaderIssueTypeNode, 'name' | 'color'> | null
  }
>

export function StickyHeader({issueKey, ...rest}: StickyHeaderProps) {
  if (!issueKey) {
    return <StickyHeaderSkeleton optionConfig={rest.optionConfig} />
  }
  return <StickyHeaderContent {...rest} issueKey={issueKey} />
}

function NavControls({optionConfig}: {optionConfig: OptionConfig}) {
  if (!optionConfig.viewNavState) return null
  return (
    <>
      <div className={styles.topContainerNavControls}>
        <ViewPositionIndicator
          navigate={optionConfig.navigate}
          viewNavState={optionConfig.viewNavState}
          onLoadMore={optionConfig.onLoadMoreNavItems}
          onLoadBack={optionConfig.onLoadBackNavItems}
          pendingFocusDirection={optionConfig.viewNavState?.pendingFocusDirection}
          pendingNavSource={optionConfig.viewNavState?.pendingNavSource}
          isStickyHeader
        />
      </div>
      <span aria-hidden="true" className={styles.topContainerExtrasDivider} />
    </>
  )
}

function StickyHeaderSkeleton({optionConfig}: {optionConfig: OptionConfig}) {
  return (
    <div data-testid={TEST_IDS.issueViewerTopContainer} className={styles.topContainer}>
      <div className={styles.topContainerLeading}>
        <NavControls optionConfig={optionConfig} />
        <SkeletonBox width="180px" height="20px" />
      </div>
      <div className={styles.topContainerExtras} aria-hidden="true">
        <div className={styles.topContainerActionGroup}>
          <SkeletonBox width="24px" height="24px" />
          <SkeletonBox width="24px" height="24px" />
          <SkeletonBox width="24px" height="24px" />
        </div>
      </div>
    </div>
  )
}

type StickyHeaderContentProps = StickyHeaderProps & {issueKey: StickyHeaderFragment}

function StickyHeaderContent({
  issueKey: data,
  linkedSecurityAlertsData,
  blockedBySecondaryData,
  trackedBySecondaryData,
  subIssueSecondaryData,
  issueNumber,
  ownerLogin,
  repoName,
  issueUrl,
  optionConfig,
  isHeaderSticky,
  onInfoClick,
  infoButtonRef,
}: StickyHeaderContentProps) {
  const {stateString} = useIssueState({
    state: data.state,
    stateReason: data.stateReason,
    options: {longText: false},
  })
  const stateOrReason =
    data.state === 'CLOSED' && (data.stateReason === 'NOT_PLANNED' || data.stateReason === 'DUPLICATE')
      ? data.stateReason
      : data.state
  const {icon: StateIcon, color: stateIconColor} = getIssueStateIcon(stateOrReason)

  const codingAssistantEnabled = isFeatureEnabled('issue_cca_modal_open')

  // At narrow viewports collapse the bell/copy/new-issue actions into a single
  // kebab menu; above the breakpoint we keep the three individual buttons.
  const [isNarrow, setIsNarrow] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia('(max-width: 768px)')
    const update = () => setIsNarrow(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])

  const showCreateButton = optionConfig.showIssueCreateButton && data.repository.isWritable

  const handleCopyLink = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(issueUrl)
    }
  }, [issueUrl])

  return (
    <div data-testid={TEST_IDS.issueViewerTopContainer} className={styles.topContainer}>
      <div className={styles.topContainerLeading}>
        <NavControls optionConfig={optionConfig} />
        <span style={{color: stateIconColor, display: 'inline-flex'}}>
          <StateIcon aria-label={stateString} />
        </span>
        <a href="#top" className={styles.topContainerTitle}>
          {data.title}
        </a>
        <span className={styles.topContainerIssueNumber}>#{issueNumber}</span>
        <div
          className={clsx(styles.topContainerChipsSlot, !isHeaderSticky && styles.topContainerChipsSlotHidden)}
          aria-hidden={!isHeaderSticky}
        >
          <StickyHeaderChips
            issueData={data}
            linkedSecurityAlertsData={linkedSecurityAlertsData}
            blockedBySecondaryData={blockedBySecondaryData}
            trackedBySecondaryData={trackedBySecondaryData}
            subIssueSecondaryData={subIssueSecondaryData}
            optionConfig={optionConfig}
          />
        </div>
      </div>
      <div className={styles.topContainerExtras}>
        {isNarrow ? (
          <>
            {isLoggedIn() && (
              <span className={styles.topContainerOverflowSlot}>
                <ActionMenu>
                  <ActionMenu.Anchor>
                    <IconButton
                      icon={KebabHorizontalIcon}
                      aria-label={BUTTON_LABELS.moreActions}
                      variant="invisible"
                      size="medium"
                    />
                  </ActionMenu.Anchor>
                  <ActionMenu.Overlay>
                    <ActionList>
                      <StickySubscribeButton
                        subscriptionData={data}
                        owner={ownerLogin}
                        repo={repoName}
                        number={issueNumber}
                        variant="menu-item"
                      />
                      <ActionList.Item onSelect={handleCopyLink}>
                        <ActionList.LeadingVisual>
                          <LinkIcon />
                        </ActionList.LeadingVisual>
                        {BUTTON_LABELS.copyIssueLink}
                      </ActionList.Item>
                      {showCreateButton && (
                        <ActionList.LinkItem href={`/${ownerLogin}/${repoName}/issues/new`}>
                          <ActionList.LeadingVisual>
                            <PlusIcon />
                          </ActionList.LeadingVisual>
                          {BUTTON_LABELS.new}
                        </ActionList.LinkItem>
                      )}
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>
              </span>
            )}
            {!isLoggedIn() && (
              <span className={styles.topContainerOverflowSlot}>
                <CopyToClipboardButton
                  textToCopy={issueUrl}
                  ariaLabel={BUTTON_LABELS.copyIssueLink}
                  icon={LinkIcon}
                  variant="invisible"
                  tooltipProps={{direction: 's'}}
                />
              </span>
            )}
          </>
        ) : (
          <div className={styles.topContainerActionGroup}>
            {isLoggedIn() && (
              <span className={styles.topContainerOverflowSlot}>
                <StickySubscribeButton
                  subscriptionData={data}
                  owner={ownerLogin}
                  repo={repoName}
                  number={issueNumber}
                />
              </span>
            )}
            <span className={styles.topContainerOverflowSlot}>
              <CopyToClipboardButton
                textToCopy={issueUrl}
                ariaLabel={BUTTON_LABELS.copyIssueLink}
                icon={LinkIcon}
                variant="invisible"
                tooltipProps={{direction: 's'}}
              />
            </span>
            {showCreateButton && (
              <span className={clsx(styles.topContainerOverflowSlot, styles.topContainerNewSlot)}>
                <Suspense fallback={<CreateIssueButtonShell iconOnly />}>
                  <StickyHeaderCreateIssueButtonLazy
                    label={BUTTON_LABELS.new}
                    variant="invisible"
                    iconOnly
                    leadingVisual={PlusIcon}
                    navigate={optionConfig.navigate}
                    optionConfig={{
                      showRepositoryPicker: false,
                      issueCreateArguments: {
                        repository: {
                          owner: ownerLogin,
                          name: repoName,
                        },
                      },
                      showFullScreenButton: true,
                      navigateToFullScreenOnTemplateChoice: true,
                      canBypassTemplateSelection: true,
                      navigate: optionConfig.navigate,
                    }}
                  />
                </Suspense>
              </span>
            )}
          </div>
        )}
        <div className={styles.topContainerActions}>
          <span aria-hidden="true" className={styles.topContainerActionsDivider} />
          <div className={styles.topContainerPrimaryCluster}>
            {codingAssistantEnabled && (
              <CodingAssistantButton
                url={issueUrl}
                buttonLabel={BUTTON_LABELS.openIn}
                promptBuilder={(url: string) => `Work on the following GitHub issue: ${url}.`}
                size="medium"
                variant="primary"
                iconOnly
              />
            )}
            <span className={styles.sidebarInfoButton}>
              <IconButton
                ref={infoButtonRef}
                icon={InfoIcon}
                aria-label="Show issue details"
                variant="default"
                size="medium"
                onClick={onInfoClick}
              />
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
